import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Play, ArrowUpRight, ArrowRight, Instagram, Youtube, Film as FilmIcon, Linkedin, X } from "lucide-react";
import { FILMS, REEL_URL, CONTACT_EMAIL, INSTAGRAM_URL, YOUTUBE_URL } from "../lib/films";
import { VideoModal } from "../components/VideoModal";

const NEON = "#E4FF00";
const NAV = [
  { label: "Work", type: "scroll", target: "work" },
  { label: "About", type: "scroll", target: "about" },
  { label: "Reel", type: "reel" },
  { label: "Contact", type: "scroll", target: "contact" },
];

function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  const lenis = window.__lenis;
  if (lenis) lenis.scrollTo(el, { offset: -80 });
  else el.scrollIntoView({ behavior: "smooth", block: "start" });
}

const TRUSTED_BY = [
  "Black Eyed Peas",
  "Judith Hill",
  "Cartoon Network",
  "YouTube",
  "Bobby Valentino",
  "Fresh Empire",
];

const HERO_STILL = "/about-jy.jpg";

const COMMERCIALS_MINI = [
  {
    id: "eDeRSrOUsjE",
    label: "Pepto Bismol Comedy Skit",
    tag: "Comedy Skit",
    image: "https://customer-assets-m6fa6gv7.emergentagent.net/job_teen-stories/artifacts/02wr5qms_peptobismolskit.png",
  },
  {
    id: "0dpZaBU59mc",
    label: "Softball Commercial",
    tag: "Commercial",
    image: "https://customer-assets-m6fa6gv7.emergentagent.net/job_teen-stories/artifacts/axz8l8eu_maddie.png",
  },
  {
    id: "5eiOhUn7GlI",
    label: "High School Football",
    tag: "Commercial",
    image: "https://customer-assets-m6fa6gv7.emergentagent.net/job_teen-stories/artifacts/rwcfhnsf_TylerThumbnail.png",
  },
  {
    id: "cEzt0ZL_JuM",
    label: "Social Media Marketing",
    tag: "Commercial",
    image: "https://customer-assets-m6fa6gv7.emergentagent.net/job_teen-stories/artifacts/whhz6v85_liz%27swerkshop1.png",
  },
  {
    id: "gWfrdpDSBZ4",
    label: "Social Media Marketing",
    tag: "Commercial",
    image: "https://customer-assets-m6fa6gv7.emergentagent.net/job_teen-stories/artifacts/v55qad4v_thumbnail-dancerevolution.png",
  },
  {
    id: "VuwG8AaxJkY",
    label: "Vans Commercial Spec",
    tag: "Commercial",
    image: "https://customer-assets-m6fa6gv7.emergentagent.net/job_teen-stories/artifacts/ku1c0qat_VHS%20Skatepark%20Moment%20Under%20Blue%20Skies.png",
  },
  {
    id: "o_PChacuSLI",
    label: "Tamron Lens Test Static Shots",
    tag: "Lens Test",
    image: "https://customer-assets-m6fa6gv7.emergentagent.net/job_teen-stories/artifacts/sqa4qodo_Cinematic%20Cap%20and%20Cinema%20Book%20Stack.png",
  },
];

const WORK_TILES = [
  ...FILMS.map((f) => ({
    id: f.id,
    title: f.title,
    tag: f.runtime.split(" · ")[0].toUpperCase() + " FILM",
    image: f.image,
    imageStyle: f.imageStyle,
    reel: f.reel,
  })),
  ...COMMERCIALS_MINI.map((c) => ({
    id: `yt-${c.id}`,
    title: c.label,
    tag: c.tag.toUpperCase(),
    image: c.image || `https://i.ytimg.com/vi/${c.id}/maxresdefault.jpg`,
    reel: `https://youtu.be/${c.id}`,
  })),
];

const PHOTOS = [
  {
    id: "photo-01",
    src: "https://customer-assets-m6fa6gv7.emergentagent.net/job_teen-stories/artifacts/a9gsvkmi_DSC01458%20copy-2.webp",
    alt: "Black and white photo of a football player standing on the field",
    series: "Field Series",
  },
  {
    id: "photo-02",
    src: "https://customer-assets-m6fa6gv7.emergentagent.net/job_teen-stories/artifacts/6dcyonom_DSC01468%20copy-2.webp",
    alt: "Black and white photo of a coach on the sideline",
    series: "Field Series",
  },
  {
    id: "photo-03",
    src: "https://customer-assets-m6fa6gv7.emergentagent.net/job_teen-stories/artifacts/u9q621ur_DSC01510-2.webp",
    alt: "Black and white photo of a football player running a route",
    series: "Field Series",
  },
  {
    id: "photo-04",
    src: "https://customer-assets-m6fa6gv7.emergentagent.net/job_teen-stories/artifacts/jm6olxlj_DSC01540%20copy-2.webp",
    alt: "Black and white photo of a player holding a Huskies helmet",
    series: "Field Series",
  },
  {
    id: "photo-05",
    src: "https://customer-assets-m6fa6gv7.emergentagent.net/job_teen-stories/artifacts/6iysmcoc_DSC01565%20copy-2.webp",
    alt: "Black and white photo of a kneeling football player holding his helmet",
    series: "Field Series",
  },
  {
    id: "photo-06",
    src: "https://customer-assets-m6fa6gv7.emergentagent.net/job_teen-stories/artifacts/34xcoik0_DSC03918.webp",
    alt: "Black and white photo of a man holding a small child at an event",
    series: "Event Series",
  },
  {
    id: "photo-07",
    src: "https://customer-assets-m6fa6gv7.emergentagent.net/job_teen-stories/artifacts/jbtncx5x_DSC03921.webp",
    alt: "Black and white profile photo of a boy wearing sunglasses",
    series: "Event Series",
  },
  {
    id: "photo-08",
    src: "https://customer-assets-m6fa6gv7.emergentagent.net/job_teen-stories/artifacts/6w6updtj_DSC03967.webp",
    alt: "Black and white photo of a young girl tying her shoe on the floor",
    series: "Event Series",
  },
  {
    id: "photo-09",
    src: "https://customer-assets-m6fa6gv7.emergentagent.net/job_teen-stories/artifacts/dqaxbeh6_DSC04005.webp",
    alt: "Black and white photo of a woman embracing a child at a candlelit event",
    series: "Event Series",
  },
  {
    id: "photo-10",
    src: "https://customer-assets-m6fa6gv7.emergentagent.net/job_teen-stories/artifacts/tq2v1xsu_DSC04207.webp",
    alt: "Black and white photo of a man dancing hand in hand with a little girl",
    series: "Event Series",
  },
];

const AnimatedIn = ({ children, delay = 0, y = 30, className }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 1.05, ease: [0.16, 1, 0.3, 1], delay }}
    className={className}
  >
    {children}
  </motion.div>
);

export default function Preview() {
  const [playing, setPlaying] = useState(null);
  const [workTab, setWorkTab] = useState("video");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!lightbox) return;
    const onKey = (e) => e.key === "Escape" && setLightbox(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox]);

  return (
    <div data-testid="portfolio-root" className="min-h-screen bg-[#050505] text-white">
      {/* NAV */}
      <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-md bg-black/40 border-b border-white/5">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 py-4 flex items-center justify-between">
          <a href="/" data-testid="mock-nav-logo" className="font-['Permanent_Marker'] text-2xl leading-none text-white hover:text-[color:var(--neon)] transition-colors" style={{ "--neon": NEON }}>
            JYT WRIGHT
          </a>
          <nav className="hidden md:flex items-center gap-10">
            {NAV.map((n) => (
              <button
                key={n.label}
                data-testid={`mock-nav-${n.label.toLowerCase()}`}
                onClick={() => {
                  if (n.type === "reel") setPlaying({ url: REEL_URL, title: "Director's Reel" });
                  else scrollToId(n.target);
                }}
                className="font-mono text-[11px] tracking-[0.32em] uppercase text-white/80 hover:text-[color:var(--neon)] transition-colors"
                style={{ "--neon": NEON }}
              >
                {n.label}
              </button>
            ))}
          </nav>
          <a
            href={`mailto:${CONTACT_EMAIL}?subject=Let%27s%20create%20something%20unforgettable`}
            data-testid="mock-cta-lets-create"
            className="inline-flex items-center gap-3 px-5 py-3 border-2 font-mono text-[11px] tracking-[0.32em] uppercase transition-colors"
            style={{ borderColor: NEON, color: NEON }}
          >
            Let's Create <ArrowUpRight size={14} />
          </a>
        </div>
      </header>

      {/* HERO */}
      <section data-testid="mock-hero" className="pt-28 md:pt-36 pb-16 md:pb-20 px-6 md:px-10">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Left copy */}
          <div className="lg:col-span-5">
            <AnimatedIn>
              <div className="font-mono text-[11px] tracking-[0.32em] uppercase mb-8" style={{ color: NEON }}>
                Director. Storyteller. Visionary.
              </div>
            </AnimatedIn>

            <AnimatedIn delay={0.1}>
              <h1
                data-testid="mock-hero-heading"
                className="font-['Anton'] uppercase text-white leading-[0.92] tracking-[-0.01em] text-[11vw] lg:text-[5vw] xl:text-[4.6vw]"
              >
                Stories that transforms<br />the soul and transcends<br />lives<span style={{ color: NEON }}>.</span>
              </h1>
            </AnimatedIn>

            <AnimatedIn delay={0.25}>
              <p className="mt-8 max-w-md font-grotesque text-base md:text-lg text-white/70 leading-relaxed">
                Jy Tristian Wright — a director and visual storyteller crafting bold, cinematic
                content that connects, inspires, and leaves a lasting impact.
              </p>
            </AnimatedIn>

            <AnimatedIn delay={0.4}>
              <button
                onClick={() => setPlaying({ url: REEL_URL, title: "Director's Reel" })}
                data-testid="mock-watch-reel"
                className="mt-10 inline-flex items-center gap-4 font-['Permanent_Marker'] text-3xl group"
                style={{ color: NEON }}
              >
                Watch Reel
                <ArrowRight
                  size={30}
                  className="translate-x-0 group-hover:translate-x-2 transition-transform"
                />
              </button>
            </AnimatedIn>
          </div>

          {/* Right featured still */}
          <div className="lg:col-span-7">
            <AnimatedIn delay={0.15} y={40}>
              <button
                onClick={() => setPlaying({ url: REEL_URL, title: "Director's Reel" })}
                data-testid="mock-hero-still"
                className="relative w-full aspect-[16/10] overflow-hidden group block"
              >
                <img src={HERO_STILL} alt="Featured reel still" className="w-full h-full object-cover" style={{ filter: "brightness(0.85)" }}/>
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span
                    className="w-24 h-24 md:w-28 md:h-28 rounded-full border-2 flex items-center justify-center group-hover:scale-110 transition-transform"
                    style={{ borderColor: NEON, color: NEON, backgroundColor: "rgba(0,0,0,0.35)" }}
                  >
                    <Play size={30} fill={NEON} />
                  </span>
                </span>
              </button>
            </AnimatedIn>

            {/* Trusted by */}
            <AnimatedIn delay={0.35}>
              <div className="mt-8 flex items-center justify-between flex-wrap gap-6 border-t border-white/10 pt-6">
                <span className="font-mono text-[10px] tracking-[0.32em] uppercase text-white/50">
                  Trusted by
                </span>
                <div className="flex items-center gap-x-8 gap-y-3 flex-wrap font-['Anton'] uppercase tracking-wide text-white/80 text-lg">
                  {TRUSTED_BY.map((t) => (
                    <span key={t} data-testid={`mock-trusted-${t.toLowerCase().replace(/\s+/g,'-')}`}>{t}</span>
                  ))}
                </div>
              </div>
            </AnimatedIn>
          </div>
        </div>
      </section>

      {/* SELECTED WORK */}
      <section id="work" data-testid="mock-work" className="py-16 md:py-24 px-6 md:px-10 border-t border-white/5">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div className="font-['Anton'] uppercase text-3xl md:text-4xl tracking-wide">Selected Work</div>
            <button className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.32em] uppercase" style={{ color: NEON }}>
              View all work <ArrowRight size={14}/>
            </button>
          </div>

          {/* Work tabs */}
          <div
            data-testid="work-tabs"
            role="tablist"
            aria-label="Work categories"
            className="flex items-center gap-8 border-b border-white/10 mb-10 md:mb-14"
          >
            {[
              { id: "video", label: "Video" },
              { id: "photo", label: "Photography" },
            ].map((t) => (
              <button
                key={t.id}
                role="tab"
                aria-selected={workTab === t.id}
                data-testid={`tab-${t.id}`}
                onClick={() => setWorkTab(t.id)}
                className={`relative py-4 font-mono text-[11px] tracking-[0.32em] uppercase transition-colors ${
                  workTab === t.id ? "text-white" : "text-white/40 hover:text-white/80"
                }`}
              >
                {t.label}
                {workTab === t.id && (
                  <motion.span
                    layoutId="work-tab-underline"
                    className="absolute left-0 right-0 -bottom-px h-[2px]"
                    style={{ backgroundColor: NEON }}
                    transition={{ type: "spring", stiffness: 320, damping: 32 }}
                  />
                )}
              </button>
            ))}
            <span className="ml-auto font-mono text-[10px] tracking-[0.32em] uppercase text-white/40">
              {workTab === "video" ? `${WORK_TILES.length} works` : `${PHOTOS.length} frames`}
            </span>
          </div>

          {workTab === "video" && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {WORK_TILES.map((w, i) => (
              <AnimatedIn key={w.id} delay={0.06 * i} y={20}>
                <button
                  onClick={() => setPlaying({ url: w.reel, title: w.title })}
                  data-testid={`mock-tile-${w.id}`}
                  className="group text-left w-full"
                >
                  <div className="relative aspect-[4/5] overflow-hidden bg-white/[0.03]">
                    <img
                      src={w.image}
                      alt={w.title}
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)]"
                      style={{ ...w.imageStyle, filter: "brightness(0.9)" }}
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                  <div className="mt-4">
                    <div className="font-['Anton'] uppercase text-lg md:text-xl tracking-wide leading-tight">{w.title}</div>
                    <div className="mt-1 font-mono text-[10px] tracking-[0.32em] uppercase" style={{ color: NEON }}>{w.tag}</div>
                  </div>
                </button>
              </AnimatedIn>
            ))}
          </div>
          )}

          {workTab === "photo" && (
            <div
              data-testid="photography-gallery"
              className="columns-1 sm:columns-2 lg:columns-3 gap-5"
            >
              {PHOTOS.map((p, i) => (
                <AnimatedIn key={p.id} delay={0.05 * i} y={24} className="mb-5 break-inside-avoid">
                  <button
                    onClick={() => setLightbox(p)}
                    data-testid={`photo-tile-${p.id}`}
                    className="group block w-full text-left"
                  >
                    <div
                      className="relative overflow-hidden border border-white/10 group-hover:border-[color:var(--neon)] transition-colors duration-500"
                      style={{ "--neon": NEON }}
                    >
                      <img
                        src={p.src}
                        alt={p.alt}
                        className="w-full h-auto block group-hover:scale-[1.03] transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)]"
                        loading="lazy"
                      />
                    </div>
                    <div className="mt-3 flex items-center justify-between font-mono text-[10px] tracking-[0.32em] uppercase text-white/50">
                      <span>N° {String(i + 1).padStart(2, "0")}</span>
                      <span style={{ color: NEON }}>B&amp;W · {p.series}</span>
                    </div>
                  </button>
                </AnimatedIn>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* DIRECTOR QUOTE */}
      <section data-testid="mock-quote" className="py-24 md:py-32 px-6 md:px-10 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto text-center">
          <AnimatedIn>
            <div className="font-mono text-[11px] tracking-[0.32em] uppercase mb-8" style={{ color: NEON }}>
              — A note from the director
            </div>
          </AnimatedIn>
          <AnimatedIn delay={0.15}>
            <blockquote
              data-testid="mock-quote-text"
              className="font-['Anton'] uppercase text-white leading-[0.95] tracking-[-0.01em] text-[9vw] md:text-[4.8vw] lg:text-[4.2vw]"
            >
              <span style={{ color: NEON }}>"</span>I direct the way I parent<span style={{ color: NEON }}>,</span><br />
              with love<span style={{ color: NEON }}>,</span> respect<span style={{ color: NEON }}>,</span> and discipline<span style={{ color: NEON }}>."</span>
            </blockquote>
          </AnimatedIn>
          <AnimatedIn delay={0.3}>
            <div className="mt-10 font-['Permanent_Marker'] text-2xl md:text-3xl" style={{ color: NEON }}>
              — Jy Tristian Wright
            </div>
          </AnimatedIn>
        </div>
      </section>

      {/* MANIFESTO STRIP */}
      <section id="about" data-testid="mock-manifesto" className="py-16 md:py-20 px-6 md:px-10 border-t border-white/10">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-center">
          <div className="md:col-span-4 font-['Permanent_Marker'] text-2xl md:text-3xl leading-tight" style={{ color: NEON }}>
            I write for the kid<br/>in the back of the theater.
          </div>
          <p className="md:col-span-5 font-grotesque text-base md:text-lg text-white/75 leading-relaxed">
            I partner with creative companies and brands to bring ideas to life through cinematic
            storytelling that resonates across culture and digital platforms.
          </p>
          <a
            href={`mailto:${CONTACT_EMAIL}`}
            data-testid="mock-cta-create"
            className="md:col-span-3 inline-flex items-center justify-between gap-4 font-mono text-[11px] tracking-[0.32em] uppercase group"
          >
            <span style={{ color: NEON }}>Let's Create Something Unforgettable</span>
            <ArrowRight size={16} style={{ color: NEON }} className="group-hover:translate-x-1 transition-transform"/>
          </a>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" data-testid="mock-contact" className="py-20 md:py-28 px-6 md:px-10 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto">
          <div className="font-mono text-[11px] tracking-[0.32em] uppercase mb-6" style={{ color: NEON }}>
            — Get in touch
          </div>
          <AnimatedIn>
            <div className="font-['Anton'] uppercase text-white leading-[0.92] tracking-[-0.01em] text-[8vw] md:text-[3.6vw]">
              Have a story that needs to find its hour<span style={{ color: NEON }}>?</span>
            </div>
          </AnimatedIn>
          <AnimatedIn delay={0.15}>
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              data-testid="mock-contact-email"
              className="mt-10 inline-flex flex-wrap items-center gap-3 font-['Permanent_Marker'] leading-[0.95] break-all lowercase hover:opacity-80 transition-opacity"
              style={{ color: NEON, fontSize: "clamp(2rem, 5.5vw, 6rem)" }}
            >
              <span>{CONTACT_EMAIL}</span>
              <ArrowUpRight size={40} />
            </a>
          </AnimatedIn>
          <div className="mt-14 flex flex-col md:flex-row md:items-center md:justify-between gap-6 border-t border-white/10 pt-8">
            <div className="font-mono text-[11px] tracking-[0.32em] uppercase text-white/70">
              Representation — Currently seeking · Direct inquiries welcome
            </div>
            <div className="flex items-center gap-6">
              <a data-testid="mock-social-instagram" href={INSTAGRAM_URL} aria-label="Instagram" className="text-white hover:opacity-70">
                <Instagram size={20} />
              </a>
              <a data-testid="mock-social-youtube" href={YOUTUBE_URL} aria-label="YouTube" className="text-white hover:opacity-70">
                <Youtube size={20} />
              </a>
              <a data-testid="mock-social-vimeo" href={REEL_URL} aria-label="Vimeo" className="text-white hover:opacity-70">
                <FilmIcon size={20} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-8 px-6 md:px-10 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="font-['Permanent_Marker'] text-xl">JYT WRIGHT</div>
        <div className="flex items-center gap-8 font-mono text-[10px] tracking-[0.32em] uppercase text-white/60">
          <a href={INSTAGRAM_URL}>Instagram</a>
          <a href={REEL_URL}>Vimeo</a>
          <a href={YOUTUBE_URL}>YouTube</a>
        </div>
        <div className="font-mono text-[10px] tracking-[0.32em] uppercase text-white/60">
          © {new Date().getFullYear()} Jyt Wright. All rights reserved.
        </div>
      </footer>

      {/* PHOTO LIGHTBOX */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            data-testid="photo-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center p-6 md:p-14"
            onClick={() => setLightbox(null)}
          >
            <button
              data-testid="photo-lightbox-close"
              aria-label="Close photo"
              className="absolute top-6 right-6 w-12 h-12 rounded-full border border-white/25 text-white inline-flex items-center justify-center hover:border-[color:var(--neon)] hover:text-[color:var(--neon)] transition-colors z-10"
              style={{ "--neon": NEON }}
            >
              <X size={18} />
            </button>
            <motion.img
              initial={{ scale: 0.96, y: 16 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.97, opacity: 0 }}
              transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
              src={lightbox.src}
              alt={lightbox.alt}
              className="max-h-[85vh] max-w-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 font-mono text-[10px] tracking-[0.32em] uppercase text-white/50">
              Press ESC or tap outside to close
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <VideoModal
        open={!!playing}
        url={playing?.url}
        title={playing?.title}
        onClose={() => setPlaying(null)}
      />
    </div>
  );
}
